import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { ObjectInfos } from "@dto/objects";
import { View } from "@dto/views";
import { getWsButtonStyle, TW_ListItemArrow, TW_ViewFolderContent, TW_ViewFolderDropTarget, TW_ViewFolderEmpty, TW_ViewFolderItem, TW_ViewFolderItemIcon, TW_ViewFolderItemText } from "@utils/twClasses";
import { FolderNode, isInSubtree, parentOf } from "@utils/viewFolders";
import { ArrowDown2, ArrowRight2, Folder } from "iconsax-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";
import DropLineIndicator from "./DropLineIndicator";
import ViewFolderMenuButton from "./ViewFolderMenuButton";

export const VIEW_DRAG_TYPE = "application/x-compozz-view";
export const FOLDER_DRAG_TYPE = "application/x-compozz-folder";

export interface FolderMoveAnchor {
    before?: string;
    after?: string;
}

interface ViewFolderItemProps {
    objectInfos: ObjectInfos;
    node: FolderNode;
    collapsedPaths: Set<string>;
    onToggleCollapsed: (path: string) => void;
    draggedFolderPath: string | null;
    onFolderDragStart: (path: string) => void;
    onFolderDragEnd: () => void;
    onAssignView: (viewKey: string, path: string | null) => void;
    onMoveFolder: (srcPath: string, destParentPath: string | null, anchor: FolderMoveAnchor | null) => void;
    renderViewItem: (view: View, containerPath: string | null) => React.ReactNode;
}

type DragOverZone = 'inside' | 'top' | 'bottom' | null;

/**
 * A personal view folder of the sidebar: collapsible header (drag source and
 * drop target) and recursive content (subfolders then views). The nesting of
 * the content containers produces the indentation.
 */
function ViewFolderItem(props: ViewFolderItemProps) {

    const { objectInfos, node, collapsedPaths, onToggleCollapsed, draggedFolderPath,
        onFolderDragStart, onFolderDragEnd, onAssignView, onMoveFolder, renderViewItem } = props;

    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const wsHoverColor = useModelStore(state => state.wsHoverColor);
    const [isHover, setIsHover] = useState(false);
    const [dragOverZone, setDragOverZone] = useState<DragOverZone>(null);

    const isCollapsed = collapsedPaths.has(node.path);
    const isEmpty = node.children.length === 0 && node.views.length === 0;

    const handleDragStart = (e: React.DragEvent) => {
        e.stopPropagation();
        e.dataTransfer.setData(FOLDER_DRAG_TYPE, node.path);
        e.dataTransfer.setData("text/plain", "folder:" + node.path);
        e.dataTransfer.effectAllowed = "move";
        onFolderDragStart(node.path);
    };

    const zoneFromEvent = (e: React.DragEvent): DragOverZone => {
        const isView = e.dataTransfer.types.includes(VIEW_DRAG_TYPE);
        const isFolder = e.dataTransfer.types.includes(FOLDER_DRAG_TYPE);
        if (!isView && !isFolder) {
            return null;
        }
        if (isView) {
            // A view can only be dropped inside the folder
            return 'inside';
        }
        // A folder dropped in its own subtree is refused
        if (draggedFolderPath && isInSubtree(node.path, draggedFolderPath)) {
            return null;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        if (y < rect.height / 4) {
            return 'top';
        }
        if (y > (rect.height * 3) / 4) {
            return 'bottom';
        }
        return 'inside';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const zone = zoneFromEvent(e);
        e.dataTransfer.dropEffect = zone === null ? "none" : "move";
        setDragOverZone(zone);
    };

    const handleDragLeave = () => {
        setDragOverZone(null);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const zone = zoneFromEvent(e);
        setDragOverZone(null);
        if (zone === null) {
            return;
        }

        const viewKey = e.dataTransfer.getData(VIEW_DRAG_TYPE);
        if (viewKey) {
            onAssignView(viewKey, node.path);
            return;
        }
        const folderPath = e.dataTransfer.getData(FOLDER_DRAG_TYPE);
        if (folderPath) {
            onFolderDragEnd();
            if (zone === 'inside') {
                onMoveFolder(folderPath, node.path, null);
            } else if (zone === 'top') {
                onMoveFolder(folderPath, parentOf(node.path), { before: node.path });
            } else {
                onMoveFolder(folderPath, parentOf(node.path), { after: node.path });
            }
        }
    };

    const highlighted = isHover || dragOverZone === 'inside';
    const style = highlighted ? getWsButtonStyle(wsHoverColor, null, wsColor) : getWsButtonStyle('transparent', null, wsColor);

    return (
        <>
            {dragOverZone === 'top' && <DropLineIndicator position="top" color={wsColor} />}
            <div
                className={`${TW_ViewFolderItem} ${dragOverZone === 'inside' ? TW_ViewFolderDropTarget : ''}`}
                style={style}
                onClick={() => onToggleCollapsed(node.path)}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
                draggable={true}
                onDragStart={handleDragStart}
                onDragEnd={onFolderDragEnd}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <Folder className={`${TW_ViewFolderItemIcon} cursor-grab`} color={wsDarkColor} />
                <TooltipProvider>
                    <Tooltip delayDuration={600}>
                        <TooltipTrigger asChild>
                            <p className={TW_ViewFolderItemText}>{node.name}</p>
                        </TooltipTrigger>
                        <TooltipContent>
                            {node.name}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <ViewFolderMenuButton objectInfos={objectInfos} path={node.path} showIcon={isHover} />
                {isCollapsed && <ArrowRight2 className={TW_ListItemArrow} />}
                {!isCollapsed && <ArrowDown2 className={TW_ListItemArrow} />}
            </div>
            {dragOverZone === 'bottom' && <DropLineIndicator position="bottom" color={wsColor} />}
            {!isCollapsed && (
                <div className={TW_ViewFolderContent}>
                    {node.children.map(child => (
                        <ViewFolderItem
                            key={child.path}
                            {...props}
                            node={child}
                        />
                    ))}
                    {node.views.map(view => renderViewItem(view, node.path))}
                    {isEmpty && <div className={TW_ViewFolderEmpty}>{t("folder.empty.text")}</div>}
                </div>
            )}
        </>
    )
}

export default ViewFolderItem;
